import { useState, useMemo, useEffect } from 'react';
import { 
  Sparkles,
  TrendingDown,
  CheckCircle2,
  Leaf,
  ShieldCheck,
  Check,
  Zap,
  JapaneseYen,
  Info,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Download,
  Building2
} from 'lucide-react';
import { EnergyDataPoint, TariffPlan, UserAccount } from '../types/energy';
import { SurchargeCalculatorCard } from './SurchargeCalculatorCard';
import { Language, translations } from '../i18n/translations';
import { buildMonthToDateUsage } from '../services/monthToDate';

const EN_WEEKDAY_SHORT = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' });
const EN_MONTH_DAY = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
const EN_MONTH_DAY_YEAR = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const EN_MONTH_YEAR = new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
const EN_MONTH_SHORT = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' });
const JA_FULL_DAY = new Intl.DateTimeFormat('ja-JP', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
const JA_MONTH_DAY = new Intl.DateTimeFormat('ja-JP', { month: 'long', day: 'numeric', timeZone: 'UTC' });
const JA_WEEKDAY_SHORT = new Intl.DateTimeFormat('ja-JP', { weekday: 'short', timeZone: 'UTC' });

// Year for a year-less portal tick (`10/14`): a leap year, so Feb 29 ticks still resolve.
const PLACEHOLDER_YEAR = 2000;

const WEEKDAY_JA_TO_EN: Record<string, string> = {
  日: 'Sun',
  月: 'Mon',
  火: 'Tue',
  水: 'Wed',
  木: 'Thu',
  金: 'Fri',
  土: 'Sat'
};

/**
 * English labels are rebuilt from parsed parts instead of returned verbatim, so a label the
 * scraper did not recognise can never put kanji into the EN UI (AI_RULES.md Rule 3.1).
 */
function stripJapaneseChars(value: string): string {
  return value.replace(/[^\x20-\x7E]/g, '').replace(/\s+/g, ' ').trim();
}

interface LabelParts {
  date: Date;
  yearKnown: boolean;
}

function dayParts(rawLabel: string, timestamp?: string): LabelParts | null {
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(timestamp || '');
  if (iso) {
    return { date: new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))), yearKnown: true };
  }
  const jaFull = /^(\d{4})年(\d{1,2})月(\d{1,2})日/.exec(rawLabel);
  if (jaFull) {
    return { date: new Date(Date.UTC(Number(jaFull[1]), Number(jaFull[2]) - 1, Number(jaFull[3]))), yearKnown: true };
  }
  const tick = /^(\d{1,2})\/(\d{1,2})/.exec(rawLabel);
  if (tick) {
    return { date: new Date(Date.UTC(PLACEHOLDER_YEAR, Number(tick[1]) - 1, Number(tick[2]))), yearKnown: false };
  }
  return null;
}

function monthParts(rawLabel: string, timestamp?: string): LabelParts | null {
  const jaFull = /^(\d{4})年(\d{1,2})月/.exec(rawLabel);
  if (jaFull) return { date: new Date(Date.UTC(Number(jaFull[1]), Number(jaFull[2]) - 1, 1)), yearKnown: true };
  const iso = /^(\d{4})-(\d{1,2})/.exec(timestamp || '');
  if (iso) return { date: new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, 1)), yearKnown: true };
  const bare = /^(\d{1,2})月?$/.exec(rawLabel.trim());
  if (bare) return { date: new Date(Date.UTC(PLACEHOLDER_YEAR, Number(bare[1]) - 1, 1)), yearKnown: false };
  return null;
}

function roundKwh(val: number | undefined | null): number {
  if (val === undefined || val === null || isNaN(val)) return 0;
  return Number(Number(val).toFixed(2));
}

interface UsageViewProps {
  account: UserAccount;
  hourlyData: EnergyDataPoint[];
  dailyData: EnergyDataPoint[];
  monthlyData: EnergyDataPoint[];
  tariff: TariffPlan;
  onNavigateToScraper: () => void;
  lang?: Language;
}

export function UsageView({
  account,
  hourlyData,
  dailyData,
  monthlyData,
  tariff,
  onNavigateToScraper,
  lang = 'ja'
}: UsageViewProps) {
  // Official SymEnergy Graph Periods: 月別 (Monthly) / 日別 (Daily) / 時間別 (Hourly)
  const [period, setPeriod] = useState<'monthly' | 'daily' | 'hourly'>('monthly');
  // Official SymEnergy Graph Mode: 電気使用量 (Usage in kWh) / 電気代 (Cost in 円)
  const [displayUnit, setDisplayUnit] = useState<'amount' | 'bill'>('amount');
  
  // Selection index for monthly / daily / hourly. A null month pin means "follow the newest month".
  const [pinnedMonthIndex, setPinnedMonthIndex] = useState<number | null>(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(dailyData.length > 0 ? dailyData.length - 1 : 0);
  const [selectedHourIndex, setSelectedHourIndex] = useState<number | null>(null);

  // Diagnostic drawer
  const [showPlanDiagnostic, setShowPlanDiagnostic] = useState(false);
  const [planSwitchToast, setPlanSwitchToast] = useState<string | null>(null);
  const [downloadToast, setDownloadToast] = useState<string | null>(null);

  const t = translations[lang];

  // A brand-new contract has no confirmed statement until its first meter reading, which the
  // portal shows as an empty 請求年月 ledger. Both facts come from the roster, not a place id.
  const activePlace = (account.contractedPlaces || []).find((place) => place.id === account.currentPlaceId);
  const isNewContractWithoutStatement =
    activePlace?.status === '契約中' && (activePlace?.confirmedStatementCount ?? 0) === 0;

  // Only ledger rows carry billing figures, so the statement table below lists statements; the
  // chart keeps every measured month, where kWh is the only thing a chart row asserts.
  const statementRows = monthlyData.filter((m) => m.statementConfirmed);

  // The portal monthly chart stops at the last finalized month, so the running month is appended
  // here from its daily telemetry. The ledger table keeps reading monthlyData, never this series,
  // so a month-to-date sum can never be presented as a statement.
  const monthlyChart = useMemo(() => {
    const monthToDate = buildMonthToDateUsage(dailyData, monthlyData);
    return monthToDate ? [...monthlyData, monthToDate] : monthlyData;
  }, [dailyData, monthlyData]);

  const selectedMonthIndex = useMemo(() => {
    const last = monthlyChart.length - 1;
    if (pinnedMonthIndex === null || pinnedMonthIndex < 0 || pinnedMonthIndex > last) return Math.max(last, 0);
    return pinnedMonthIndex;
  }, [pinnedMonthIndex, monthlyChart.length]);

  const formatDayLabel = (rawLabel: string, timestamp?: string) => {
    if (!rawLabel && !timestamp) return '';
    if (lang === 'ja') return rawLabel;

    const parts = dayParts(rawLabel, timestamp);
    if (!parts) return stripJapaneseChars(rawLabel);

    const weekdayChar = /\((.)\)/.exec(rawLabel)?.[1] ?? '';
    // A year-less tick cannot be dated, so its weekday only comes from the portal's own `(曜)`.
    const weekday =
      WEEKDAY_JA_TO_EN[weekdayChar] || (parts.yearKnown ? EN_WEEKDAY_SHORT.format(parts.date) : '');
    const dateText = parts.yearKnown ? EN_MONTH_DAY_YEAR.format(parts.date) : EN_MONTH_DAY.format(parts.date);

    return weekday ? `${dateText} (${weekday})` : dateText;
  };

  const formatMonthLabel = (rawLabel: string, timestamp?: string) => {
    if (!rawLabel && !timestamp) return '';
    if (lang === 'ja') return rawLabel;

    const parts = monthParts(rawLabel, timestamp);
    if (!parts) return stripJapaneseChars(rawLabel);
    return parts.yearKnown ? EN_MONTH_YEAR.format(parts.date) : EN_MONTH_SHORT.format(parts.date);
  };

  const formatReadingDate = (dateStr?: string) => {
    if (!dateStr || dateStr === '-') return '-';
    if (lang === 'ja') return dateStr;
    const match = /(\d{1,2})月(\d{1,2})日/.exec(dateStr);
    if (match) {
      return EN_MONTH_DAY.format(new Date(Date.UTC(PLACEHOLDER_YEAR, Number(match[1]) - 1, Number(match[2]))));
    }
    return stripJapaneseChars(dateStr);
  };

  const getMonthShortAbbr = (label: string, ts: string) => {
    if (lang === 'ja') return label.replace(/^\d{4}年/, '').replace(/^0/, '');
    const parts = monthParts(label, ts);
    return parts ? EN_MONTH_SHORT.format(parts.date) : stripJapaneseChars(label);
  };

  /**
   * Period captions want the calendar year, which chart/table labels omit, and they want it in the
   * active locale — the old JA caption spliced `2026年` onto an `M/D` tick, yielding `2026年10/14日`.
   */
  const formatPeriodDay = (row?: EnergyDataPoint) => {
    if (!row) return '';
    const parts = dayParts(row.label || '', row.timestamp);
    if (!parts) return lang === 'ja' ? row.label || '' : stripJapaneseChars(row.label || '');

    const weekdayChar = /\((.)\)/.exec(row.label || '')?.[1] ?? '';
    if (lang === 'ja') {
      const weekday = weekdayChar || JA_WEEKDAY_SHORT.format(parts.date);
      return `${JA_FULL_DAY.format(parts.date)}(${weekday})`;
    }
    const weekday = WEEKDAY_JA_TO_EN[weekdayChar] || EN_WEEKDAY_SHORT.format(parts.date);
    return `${EN_MONTH_DAY_YEAR.format(parts.date)} (${weekday})`;
  };

  // Dynamic list of available dates for hourly view
  const availableHourlyDates = useMemo(() => {
    if (!dailyData || dailyData.length === 0) return [];

    return dailyData.map((d) => {
      const raw = d.label || '';
      const weekdayChar = /\((.)\)/.exec(raw)?.[1] ?? '';
      const parts = dayParts(raw, d.timestamp);
      const weekdayEn =
        WEEKDAY_JA_TO_EN[weekdayChar] || (parts?.yearKnown ? EN_WEEKDAY_SHORT.format(parts.date) : '');

      const dateJa = parts ? (parts.yearKnown ? JA_FULL_DAY.format(parts.date) : JA_MONTH_DAY.format(parts.date)) : raw;
      const dateEn = parts
        ? (parts.yearKnown ? EN_MONTH_DAY_YEAR.format(parts.date) : EN_MONTH_DAY.format(parts.date))
        : stripJapaneseChars(raw);

      const labelJa = `${dateJa}${weekdayChar ? `(${weekdayChar})` : ''}`;
      const labelEn = `${weekdayEn ? `${weekdayEn}, ` : ''}${dateEn}`;
      const isWeekend = raw.includes('土') || raw.includes('日') || raw.includes('Sat') || raw.includes('Sun');

      return { raw, labelJa, labelEn, isWeekend, dayData: d };
    });
  }, [dailyData]);

  const [hourlyDateIdx, setHourlyDateIdx] = useState<number>(0);

  useEffect(() => {
    if (availableHourlyDates.length > 0) {
      const lastWithData = availableHourlyDates.findLastIndex((item) => (item.dayData?.kwh || 0) > 0);
      setHourlyDateIdx(lastWithData >= 0 ? lastWithData : availableHourlyDates.length - 1);
    } else {
      setHourlyDateIdx(0);
    }
  }, [availableHourlyDates]);

  const currentHourlyObj = availableHourlyDates[hourlyDateIdx] || null;
  const hourlyDateStr = currentHourlyObj ? (lang === 'ja' ? currentHourlyObj.labelJa : currentHourlyObj.labelEn) : '';
  const isHourlyWeekend = currentHourlyObj?.isWeekend ?? false;

  const handleToggleHourlyDate = (direction: 'prev' | 'next') => {
    if (availableHourlyDates.length === 0) return;
    let nextIdx = direction === 'next' ? hourlyDateIdx + 1 : hourlyDateIdx - 1;
    if (nextIdx < 0) nextIdx = availableHourlyDates.length - 1;
    if (nextIdx >= availableHourlyDates.length) nextIdx = 0;
    setHourlyDateIdx(nextIdx);
  };

  // 24-hour interval dataset calculated dynamically for the selected day (NO fake fallbacks)
  const activeHourlyData: EnergyDataPoint[] = useMemo(() => {
    if (!currentHourlyObj) {
      return Array.isArray(hourlyData) ? hourlyData : [];
    }
    const selectedDay = currentHourlyObj.dayData;

    // 1. If hourlyData prop contains exact interval readings for this day, use them directly
    if (selectedDay && Array.isArray(hourlyData) && hourlyData.length > 0) {
      const dayIso = selectedDay.timestamp || '';
      const daySlash = (selectedDay.label || '').split('(')[0];

      const matchedPoints = hourlyData.filter((h) => {
        if (!h.timestamp) return false;
        if (dayIso && h.timestamp.startsWith(dayIso)) return true;
        if (daySlash && (h.timestamp.includes(daySlash) || (h.label && h.label.includes(daySlash)))) return true;
        return false;
      });

      if (matchedPoints.length >= 24) {
        return matchedPoints.slice(0, 24);
      }
      if (matchedPoints.length > 0) {
        return matchedPoints;
      }
    }

    if (!selectedDay || (selectedDay.kwh || 0) <= 0) {
      const hours = 24;
      const pts: EnergyDataPoint[] = [];
      for (let i = 0; i < hours; i++) {
        const hourStr = `${i.toString().padStart(2, '0')}:00`;
        pts.push({
          timestamp: `${selectedDay?.timestamp || '2026-10-14'} ${hourStr}`,
          label: hourStr,
          kwh: 0,
          cost: 0,
          isPeak: false,
          dayKwh: 0,
          lifeKwh: 0,
          nightKwh: 0
        });
      }
      return pts;
    }

    const isWeekend = currentHourlyObj.isWeekend;
    const dayKwh = selectedDay.dayKwh || 0;
    const lifeKwh = selectedDay.lifeKwh || 0;
    const nightKwh = selectedDay.nightKwh || 0;

    const dayHours = !isWeekend ? 7 : 14;
    const lifeHours = !isWeekend ? 10 : 0;
    const nightHours = !isWeekend ? 7 : 10;

    const pts: EnergyDataPoint[] = [];
    const hours = 24;
    for (let i = 0; i < hours; i++) {
      let tier: 'day' | 'life' | 'night' = 'night';
      if (!isWeekend) {
        if (i >= 23 || i < 6) tier = 'night';
        else if ((i >= 6 && i < 9) || (i >= 16 && i < 23)) tier = 'life';
        else tier = 'day';
      } else {
        if (i >= 22 || i < 8) tier = 'night';
        else tier = 'day';
      }

      let hourKwh = 0;
      let rate = 18.88;
      if (tier === 'day') {
        hourKwh = dayHours > 0 ? Number((dayKwh / dayHours).toFixed(2)) : 0;
        rate = 26.25;
      } else if (tier === 'life') {
        hourKwh = lifeHours > 0 ? Number((lifeKwh / lifeHours).toFixed(2)) : 0;
        rate = 32.65;
      } else {
        hourKwh = nightHours > 0 ? Number((nightKwh / nightHours).toFixed(2)) : 0;
        rate = 18.88;
      }

      const cost = Math.round(hourKwh * rate);
      const hourStr = `${i.toString().padStart(2, '0')}:00`;
      pts.push({
        timestamp: `${selectedDay?.timestamp || '2026-10-14'} ${hourStr}`,
        label: hourStr,
        kwh: hourKwh,
        cost,
        isPeak: tier === 'life',
        tier,
        tierRate: rate
      });
    }
    return pts;
  }, [currentHourlyObj, hourlyData]);

  // Clamp selection indices when data arrays change
  useEffect(() => {
    if (Array.isArray(dailyData) && dailyData.length > 0) {
      if (selectedDayIndex >= dailyData.length || selectedDayIndex < 0) {
        setSelectedDayIndex(dailyData.length - 1);
      }
    } else {
      setSelectedDayIndex(0);
    }
  }, [dailyData, selectedDayIndex]);

  // Selected month data point (null if empty, NO fake fallback)
  const activeMonth: EnergyDataPoint | null = useMemo(() => {
    if (Array.isArray(monthlyChart) && monthlyChart.length > 0) {
      if (selectedMonthIndex >= 0 && selectedMonthIndex < monthlyChart.length && monthlyChart[selectedMonthIndex]) {
        return monthlyChart[selectedMonthIndex];
      }
      return monthlyChart[monthlyChart.length - 1] || null;
    }
    return null;
  }, [monthlyChart, selectedMonthIndex]);

  // Selected day data point (null if empty, NO fake fallback)
  const activeDay: EnergyDataPoint | null = useMemo(() => {
    if (Array.isArray(dailyData) && dailyData.length > 0) {
      if (selectedDayIndex >= 0 && selectedDayIndex < dailyData.length && dailyData[selectedDayIndex]) {
        return dailyData[selectedDayIndex];
      }
      return dailyData[dailyData.length - 1] || null;
    }
    return null;
  }, [dailyData, selectedDayIndex]);

  // Daily totals for 3 tiers in daily view
  const dailyTotals = useMemo(() => {
    if (!dailyData || dailyData.length === 0) {
      return { day: 0, life: 0, night: 0, total: 0, avg: 0 };
    }
    const daySum = dailyData.reduce((acc, d) => acc + (d.dayKwh || 0), 0);
    const lifeSum = dailyData.reduce((acc, d) => acc + (d.lifeKwh || 0), 0);
    const nightSum = dailyData.reduce((acc, d) => acc + (d.nightKwh || 0), 0);
    const totalSum = dailyData.reduce((acc, d) => acc + (d?.kwh || 0), 0);
    return {
      day: Number(daySum.toFixed(2)),
      life: Number(lifeSum.toFixed(2)),
      night: Number(nightSum.toFixed(2)),
      total: Number(totalSum.toFixed(1)),
      avg: Number((totalSum / dailyData.length).toFixed(1))
    };
  }, [dailyData]);

  // Hourly total usage
  const hourlyTotalKwh = useMemo(() => {
    if (!activeHourlyData || activeHourlyData.length === 0) return 0;
    return Number(activeHourlyData.reduce((acc, d) => acc + d.kwh, 0).toFixed(1));
  }, [activeHourlyData]);

  // Max value for monthly chart scaling
  const maxMonthlyVal = useMemo(() => {
    if (!monthlyChart || monthlyChart.length === 0) return displayUnit === 'amount' ? 100 : 5000;
    if (displayUnit === 'amount') {
      const maxKwh = Math.max(...monthlyChart.map(d => Math.max(d.kwh, d.prevYearKwh || 0)), 100);
      return Math.ceil(maxKwh / 50) * 50;
    } else {
      const maxCost = Math.max(...monthlyChart.map(d => Math.max(d.cost, d.prevYearCost || 0)), 5000);
      return Math.ceil(maxCost / 2000) * 2000;
    }
  }, [monthlyChart, displayUnit]);

  // Max value for daily chart scaling
  const maxDailyVal = useMemo(() => {
    if (!dailyData || dailyData.length === 0) return 2.5;
    const maxVal = Math.max(...dailyData.map(d => d?.kwh || 0), 2.5);
    return Math.ceil(maxVal);
  }, [dailyData]);

  // Max value for hourly chart scaling
  const maxHourlyVal = useMemo(() => {
    if (!activeHourlyData || activeHourlyData.length === 0) return 1.0;
    const maxVal = Math.max(...activeHourlyData.map(d => d?.kwh || 0), 0.1);
    return Math.max(0.4, Number((maxVal * 1.2).toFixed(1)));
  }, [activeHourlyData]);

  // Official SymEnergy MyPage Feature: ぴったりプラン診断 (Fit Plan Diagnostic simulation)
  const currentTotalKwh = activeMonth?.kwh ?? 0;
  const estimatedDayFitPlanCost = currentTotalKwh > 0 ? Math.round(currentTotalKwh * (0.55 * 24.20 + 0.45 * 29.50) + 1080) : 0;
  const estimatedNightFitPlanCost = currentTotalKwh > 0 ? Math.round(
    currentTotalKwh * (0.28 * 26.25 + 0.42 * 32.65 + 0.30 * 18.88) + 1080
  ) : 0;
  const estimatedBasicPlanCost = currentTotalKwh > 0 ? Math.round(currentTotalKwh * 29.80 + 1080) : 0;

  const isDayFitBest = estimatedDayFitPlanCost <= estimatedNightFitPlanCost;
  const bestPlanName = isDayFitBest ? t.planDayFitName : t.planNightFitName;
  const annualSavingsCalc = currentTotalKwh > 0 ? Math.abs(estimatedBasicPlanCost - Math.min(estimatedDayFitPlanCost, estimatedNightFitPlanCost)) * 12 : 0;

  const handleSimulateSwitch = (planName: string) => {
    setPlanSwitchToast(planName);
    setTimeout(() => {
      setPlanSwitchToast(null);
    }, 3500);
  };

  const handleDownloadCsv = (periodLabel: string) => {
    setDownloadToast(lang === 'ja' ? `${periodLabel} のCSVデータをダウンロードしました` : `Downloaded CSV for ${periodLabel}`);
    setTimeout(() => {
      setDownloadToast(null);
    }, 3000);
  };

  return (
    <div className="space-y-5 pb-8">
      {/* Toast Notification for CSV Download */}
      {downloadToast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 bg-teal-900/95 text-white border border-teal-500/50 px-4 py-2 rounded-2xl shadow-xl flex items-center gap-2 text-xs font-semibold animate-fadeIn backdrop-blur-md">
          <Download className="w-4 h-4 text-teal-300" />
          <span>{downloadToast}</span>
        </div>
      )}

      {/* Official Header: 使用量グラフ & Notice (SymEnergy Official Spec) */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 border border-slate-800 p-4 sm:p-5 shadow-xl">
        <div className="absolute top-0 right-0 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-teal-500/15 border border-teal-500/30 text-teal-400 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-extrabold text-white">
                  {lang === 'ja' ? '使用量グラフ' : 'Energy Usage & Cost'}
                </span>
                <span className="text-[10px] text-teal-300 font-semibold bg-teal-950 border border-teal-800/40 rounded px-2 py-0.5">
                  {lang === 'ja' ? 'シン・エナジー公式マイページ準拠' : 'Official SymEnergy Spec'}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                {lang === 'ja' ? account.tariff.name : 'SymEnergy 【Night Lifestyle Fit Plan】'} (50A / {lang === 'ja' ? account.tariff.area : 'TEPCO Grid'}) · {lang === 'ja' ? '地点:' : 'Supply Point:'} {account.tariff.mprnNumber}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onNavigateToScraper}
            className="self-start sm:self-auto text-xs font-semibold text-teal-400 hover:text-teal-300 bg-teal-950/70 hover:bg-teal-900/50 border border-teal-800/60 rounded-xl px-3 py-1.5 transition-colors shrink-0 flex items-center gap-1.5"
          >
            <span>{t.portalSyncBtn}</span>
          </button>
        </div>

        {/* Transmission Operator Notice: SymEnergy does not provide smart meter today telemetry */}
        <div className="flex items-start gap-2 bg-slate-950/70 border border-slate-800/80 rounded-xl px-3 py-2 text-xs text-slate-400 leading-relaxed">
          <Info className="w-3.5 h-3.5 text-teal-400 shrink-0 mt-0.5" />
          <div className="text-[11px]">
            <p>{t.gridSpecNoticeP1}</p>
            <p>{t.gridSpecNoticeP2}</p>
          </div>
        </div>

        {/* 3-Tier Weekday Pricing Card (デイ・ライフ・ナイト) */}
        <div className="mt-3.5 pt-3.5 border-t border-slate-800/80">
          <div className="flex items-center justify-between text-xs text-slate-300 mb-2">
            <span className="font-bold flex items-center gap-1 text-white">
              <span>{t.contractRateTier}</span>
            </span>
            <span className="text-[10px] text-teal-400 font-medium">
              {t.baseChargeMonthly}
            </span>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center font-mono text-xs">
            {/* Night Time */}
            <div className="bg-slate-950/70 rounded-2xl p-2.5 border border-blue-900/40 relative overflow-hidden">
              <div className="w-full h-1 bg-[#3071b7] absolute top-0 left-0" />
              <span className="text-[10px] text-blue-400 font-sans font-bold block">{t.nightTime}</span>
              <span className="text-base font-extrabold text-white block mt-0.5">¥18.88</span>
              <span className="text-[9px] text-slate-400 font-sans block">{t.nightTimeWeekdayDesc}</span>
            </div>

            {/* Day Time */}
            <div className="bg-slate-950/70 rounded-2xl p-2.5 border border-amber-900/40 relative overflow-hidden">
              <div className="w-full h-1 bg-[#f5ad6a] absolute top-0 left-0" />
              <span className="text-[10px] text-amber-400 font-sans font-bold block">{t.dayTime}</span>
              <span className="text-base font-extrabold text-white block mt-0.5">¥26.25</span>
              <span className="text-[9px] text-slate-400 font-sans block">{t.dayTimeWeekdayDesc}</span>
            </div>

            {/* Life Time */}
            <div className="bg-slate-950/70 rounded-2xl p-2.5 border border-emerald-900/40 relative overflow-hidden">
              <div className="w-full h-1 bg-[#8eb367] absolute top-0 left-0" />
              <span className="text-[10px] text-emerald-400 font-sans font-bold block">{t.lifeTime}</span>
              <span className="text-base font-extrabold text-white block mt-0.5">¥32.65</span>
              <span className="text-[9px] text-slate-400 font-sans block">{t.lifeTimeWeekdayDesc}</span>
            </div>
          </div>
          <p className="text-[10px] text-slate-400 mt-2">
            {t.weekend2TierNotice}
          </p>
        </div>
      </div>

      {/* New-contract notice banner: active place whose statement ledger is still empty */}
      {isNewContractWithoutStatement && (
        <div className="p-4 rounded-3xl bg-blue-950/40 border border-blue-800/60 flex items-start gap-3">
          <Building2 className="w-5 h-5 text-blue-400 shrink-0 mt-0.5" />
          <div className="text-xs text-blue-200 leading-relaxed">
            <p className="font-bold text-white text-sm">
              {t.activeLocationNotice1}
            </p>
            <p className="mt-1 text-slate-300">
              {t.activeLocationNotice2}
            </p>
            <p className="mt-0.5 text-slate-400">
              {t.activeLocationNotice3}
            </p>
          </div>
        </div>
      )}

      {/* ぴったりプラン診断 (Optimal Plan Diagnostic) Banner */}
      {/* Requirement: In mobile view, put run plan diagnostic under, not on right side */}
      <div className="bg-gradient-to-r from-teal-950/70 via-slate-900 to-slate-900 border border-teal-800/50 rounded-3xl p-4 sm:p-5 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start sm:items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-teal-500/20 text-teal-300 flex items-center justify-center shrink-0 mt-0.5 sm:mt-0">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm font-bold text-white">
                  {t.diagnosticTitle}
                </span>
                <span className="text-[10px] font-semibold text-teal-400 bg-teal-950 border border-teal-800/50 rounded px-1.5 py-0.5 whitespace-nowrap">
                  {lang === 'ja' ? 'マイページ公式機能' : 'Official FAQ'}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5">
                {lang === 'ja'
                  ? `直近の実績データから判定：${bestPlanName} が最も経済的です`
                  : `Analyzed from confirmed interval telemetry: ${bestPlanName} is most economical`}
              </p>
            </div>
          </div>

          {/* In mobile view, button is placed UNDER the text, full-width on mobile */}
          <button
            type="button"
            onClick={() => setShowPlanDiagnostic(!showPlanDiagnostic)}
            className="w-full sm:w-auto mt-2 sm:mt-0 text-center text-xs font-semibold text-teal-300 hover:text-white bg-teal-950/90 hover:bg-teal-900 px-4 py-2 rounded-xl border border-teal-700/60 hover:border-teal-500 transition-colors shrink-0 shadow-sm"
          >
            {showPlanDiagnostic ? t.diagnosticCloseBtn : t.diagnosticBtn}
          </button>
        </div>

        {/* Diagnostic breakdown drawer */}
        {showPlanDiagnostic && (
          <div className="mt-4 pt-3 border-t border-slate-800/80 space-y-3 animate-fadeIn">
            <div className="flex items-center justify-between text-xs text-slate-300 bg-slate-950/50 p-2.5 rounded-xl border border-slate-800">
              <span>{t.diagnosticAnalysis}</span>
              <span className="font-bold text-emerald-400">
                {t.diagnosticAnnualSavings.replace('{val}', annualSavingsCalc.toLocaleString())}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs">
              {/* Day Fit Plan */}
              <div className={`p-3 rounded-2xl border relative flex flex-col justify-between ${
                isDayFitBest 
                  ? 'bg-slate-950/90 border-teal-500/50 ring-1 ring-teal-500/30' 
                  : 'bg-slate-950/50 border-slate-800'
              }`}>
                <div>
                  <div className="flex justify-between items-center">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${
                      isDayFitBest ? 'text-teal-400' : 'text-slate-500'
                    }`}>
                      {isDayFitBest ? t.diagnosticBestTag : t.compareTag}
                    </span>
                  </div>
                  <p className="font-bold text-white text-xs mt-1">{t.planDayFitName}</p>
                  <p className="text-base font-extrabold text-teal-300 font-mono mt-0.5">
                    約 ¥{estimatedDayFitPlanCost.toLocaleString()} / 月
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1">{t.planDayFitDesc}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleSimulateSwitch(t.planDayFitName)}
                  className="mt-3 w-full py-1.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold text-[11px] rounded-lg transition-colors"
                >
                  {t.diagnosticSwitchBtn.replace('{plan}', t.planDayFitName)}
                </button>
              </div>

              {/* Night Fit Plan */}
              <div className={`p-3 rounded-2xl border relative flex flex-col justify-between ${
                !isDayFitBest 
                  ? 'bg-slate-950/90 border-teal-500/50 ring-1 ring-teal-500/30' 
                  : 'bg-slate-950/50 border-slate-800'
              }`}>
                <div>
                  <div className="flex justify-between items-center">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${
                      !isDayFitBest ? 'text-teal-400' : 'text-slate-500'
                    }`}>
                      {!isDayFitBest ? t.diagnosticBestTag : t.compareTag}
                    </span>
                    <span className="text-[9px] bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded font-medium">
                      {t.diagnosticCurrentTag}
                    </span>
                  </div>
                  <p className="font-bold text-white text-xs mt-1">{t.planNightFitName}</p>
                  <p className="text-base font-extrabold text-teal-300 font-mono mt-0.5">
                    約 ¥{estimatedNightFitPlanCost.toLocaleString()} / 月
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1">{t.planNightFitDesc}</p>
                </div>
                <button
                  type="button"
                  disabled
                  className="mt-3 w-full py-1.5 bg-slate-800 text-slate-400 font-bold text-[11px] rounded-lg border border-slate-700"
                >
                  {t.currentPlanTag}
                </button>
              </div>

              {/* Basic Plan */}
              <div className="p-3 rounded-2xl bg-slate-950/50 border border-slate-800 flex flex-col justify-between">
                <div>
                  <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">
                    {t.standardTag}
                  </span>
                  <p className="font-bold text-slate-300 text-xs mt-1">{t.planBasicName}</p>
                  <p className="text-base font-extrabold text-slate-300 font-mono mt-0.5">
                    約 ¥{estimatedBasicPlanCost.toLocaleString()} / 月
                  </p>
                  <p className="text-[10px] text-slate-500 mt-1">{t.planBasicDesc}</p>
                </div>
                <button
                  type="button"
                  onClick={() => handleSimulateSwitch(t.planBasicName)}
                  className="mt-3 w-full py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] rounded-lg transition-colors"
                >
                  {t.diagnosticSwitchBtn.replace('{plan}', t.planBasicName)}
                </button>
              </div>
            </div>

            {planSwitchToast && (
              <div className="p-2.5 rounded-xl bg-teal-950/80 border border-teal-500/40 text-xs text-teal-300 flex items-center gap-2 animate-fadeIn">
                <Check className="w-4 h-4 text-teal-400 shrink-0" />
                <span>{t.diagnosticSwitchSuccess} ({planSwitchToast})</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Graph Card matching SymEnergy MyPage */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-4 sm:p-5 shadow-xl">
        {/* Navigation Row: Period Toggles and Unit Toggles */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          {/* Period selector: 月別 (Monthly) | 日別 (Daily) | 時間別 (Hourly) */}
          <div className="flex items-center p-1 bg-slate-950/70 rounded-xl border border-slate-800/80 overflow-x-auto">
            <button
              type="button"
              onClick={() => { setPeriod('monthly'); setSelectedHourIndex(null); }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all whitespace-nowrap ${
                period === 'monthly'
                  ? 'bg-teal-500 text-slate-950 shadow-md font-bold'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {t.periodMonthly}
            </button>
            <button
              type="button"
              onClick={() => { setPeriod('daily'); setSelectedHourIndex(null); }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all whitespace-nowrap ${
                period === 'daily'
                  ? 'bg-teal-500 text-slate-950 shadow-md font-bold'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {t.periodDaily}
            </button>
            <button
              type="button"
              onClick={() => { setPeriod('hourly'); }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all whitespace-nowrap ${
                period === 'hourly'
                  ? 'bg-teal-500 text-slate-950 shadow-md font-bold'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {t.periodHourlyTab}
            </button>
          </div>

          {/* Unit Toggle (電気使用量 / kWh vs 電気代 / 円) */}
          {/* Requirement: In mobile view, use icon for tabgroup for usage and cost to save space */}
          <div className="flex items-center p-1 bg-slate-950/70 rounded-xl border border-slate-800/80 self-end sm:self-auto shrink-0">
            <button
              type="button"
              onClick={() => setDisplayUnit('amount')}
              className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                displayUnit === 'amount'
                  ? 'bg-teal-500 text-slate-950 shadow-md font-bold'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
              title={t.unitKwh}
              aria-label={t.usageUnitLabel}
            >
              <Zap className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">{t.usageUnitLabel}</span>
            </button>
            <button
              type="button"
              onClick={() => setDisplayUnit('bill')}
              className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                displayUnit === 'bill'
                  ? 'bg-teal-500 text-slate-950 shadow-md font-bold'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
              title={t.unitCost}
              aria-label={t.costUnitLabel}
            >
              <JapaneseYen className="w-4 h-4 shrink-0" />
              <span className="hidden sm:inline">{t.costUnitLabel}</span>
            </button>
          </div>
        </div>

        {/* Period Header Controls & Legends */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 pb-4 border-b border-slate-800 mb-4">
          <div>
            {/* View Title & Period Indicator */}
            <div className="text-xs text-slate-400 font-medium flex items-center gap-2 flex-wrap">
              {period === 'monthly' && (
                monthlyChart && monthlyChart.length > 0
                  ? t.periodRangeMonthly
                      .replace('{start}', formatMonthLabel(monthlyChart[0]?.label || '', monthlyChart[0]?.timestamp))
                      .replace('{end}', formatMonthLabel(monthlyChart[monthlyChart.length - 1]?.label || '', monthlyChart[monthlyChart.length - 1]?.timestamp))
                  : (lang === 'ja' ? 'データがありません' : 'No Data Available')
              )}
              {period === 'daily' && (
                dailyData && dailyData.length > 0
                  ? t.periodRangeDaily
                      .replace('{start}', formatPeriodDay(dailyData[0]))
                      .replace('{end}', formatPeriodDay(dailyData[dailyData.length - 1]))
                      .replace('{days}', String(dailyData.length))
                  : (lang === 'ja' ? 'データがありません' : 'No Data Available')
              )}
              {period === 'hourly' && (
                availableHourlyDates && availableHourlyDates.length > 0
                  ? (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleToggleHourlyDate('prev')}
                        className="px-2 py-0.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 transition-colors flex items-center gap-1 text-[11px]"
                        title={lang === 'ja' ? '前日へ' : 'Previous Day'}
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                        <span>{lang === 'ja' ? '前日' : 'Prev'}</span>
                      </button>
                      <span className="font-bold text-white">{t.targetDateLabel}{hourlyDateStr}</span>
                      <button
                        type="button"
                        onClick={() => handleToggleHourlyDate('next')}
                        className="px-2 py-0.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 transition-colors flex items-center gap-1 text-[11px]"
                        title={lang === 'ja' ? '翌日へ' : 'Next Day'}
                      >
                        <span>{lang === 'ja' ? '翌日' : 'Next'}</span>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )
                  : (lang === 'ja' ? 'データがありません' : 'No Data Available')
              )}
            </div>

            {/* Main Metric Value */}
            <div className="flex items-baseline gap-3 mt-1">
              <span className="text-3xl font-extrabold text-white font-mono tracking-tight">
                {period === 'monthly' && (
                  displayUnit === 'amount' 
                    ? `${(activeMonth?.kwh ?? 0).toFixed(1)} kWh` 
                    : `¥${(activeMonth?.cost ?? 0).toLocaleString()}`
                )}
                {period === 'daily' && `${(activeDay?.kwh ?? 0).toFixed(1)} kWh`}
                {period === 'hourly' && `${hourlyTotalKwh} kWh`}
              </span>

              {period === 'monthly' && (
                <div className="text-xs font-mono text-slate-400">
                  <span className="block text-[10px] text-slate-500">{lang === 'ja' ? '前年同月' : 'Last Year'}</span>
                  <span className="text-slate-300">
                    {displayUnit === 'amount'
                      ? `${activeMonth?.prevYearKwh || 0} kWh`
                      : `¥${(activeMonth?.prevYearCost || 0).toLocaleString()}`}
                  </span>
                </div>
              )}
            </div>

            {/* Sub-label for selected item */}
            <p className="text-[11px] text-teal-400 font-mono mt-0.5">
              {period === 'monthly' && (
                activeMonth?.monthToDate
                  ? (lang === 'ja' ? `${activeMonth?.label || ''} ${t.monthToDateLabel}` : `${formatMonthLabel(activeMonth?.label || '', activeMonth?.timestamp)} ${t.monthToDateLabel}`)
                  : (lang === 'ja' ? `${activeMonth?.label || ''} 確定値` : `${formatMonthLabel(activeMonth?.label || '', activeMonth?.timestamp)} ${t.finalizedLabel}`)
              )}
              {period === 'daily' && (lang === 'ja' ? `${activeDay?.label || ''} 30分積算` : `${formatDayLabel(activeDay?.label || '')} ${t.integrated30Min}`)}
              {period === 'hourly' && t.hourlyFlashTitle}
            </p>
          </div>

          {/* Top Right Legend matching SymEnergy portal */}
          {period === 'monthly' && displayUnit === 'amount' && (
            <div className="bg-slate-950/70 p-2.5 rounded-2xl border border-slate-800 text-[10px] self-start font-sans">
              <table className="text-center border-separate border-spacing-x-2 border-spacing-y-1">
                <thead>
                  <tr className="text-slate-400 font-semibold">
                    <th></th>
                    <th className="bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded">{lang === 'ja' ? '前年' : 'Last Year'}</th>
                    <th className="bg-slate-800 text-slate-300 px-1.5 py-0.5 rounded">{lang === 'ja' ? '今年' : 'This Year'}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-right text-slate-300 font-medium">{t.dayTime}</td>
                    <td><span className="inline-block w-4 h-2.5 rounded-sm bg-[#fec794]" /></td>
                    <td><span className="inline-block w-4 h-2.5 rounded-sm bg-[#f5ad6a]" /></td>
                  </tr>
                  <tr>
                    <td className="text-right text-slate-300 font-medium">{t.lifeTime}</td>
                    <td><span className="inline-block w-4 h-2.5 rounded-sm bg-[#badf92]" /></td>
                    <td><span className="inline-block w-4 h-2.5 rounded-sm bg-[#8eb367]" /></td>
                  </tr>
                  <tr>
                    <td className="text-right text-slate-300 font-medium">{t.nightTime}</td>
                    <td><span className="inline-block w-4 h-2.5 rounded-sm bg-[#7b9ec3]" /></td>
                    <td><span className="inline-block w-4 h-2.5 rounded-sm bg-[#3071b7]" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {period === 'monthly' && displayUnit === 'bill' && (
            <div className="bg-slate-950/70 px-3 py-2 rounded-2xl border border-slate-800 text-xs flex items-center gap-3">
              <span className="flex items-center gap-1.5 text-slate-300">
                <span className="w-3 h-3 rounded-full bg-[#ed7170]" />
                <span>{t.thisYearTax}</span>
              </span>
              <span className="flex items-center gap-1.5 text-slate-400">
                <span className="w-3 h-3 rounded-full bg-[#82acbf]" />
                <span>{t.lastYearTax}</span>
              </span>
            </div>
          )}

          {period === 'daily' && (
            <div className="bg-slate-950/70 p-2.5 rounded-2xl border border-slate-800 text-[10px] self-start">
              <span className="text-slate-400 font-medium block text-center mb-1">{t.periodTotalDays.replace('{days}', '31')}</span>
              <div className="space-y-1 font-mono">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-1 text-slate-300">
                    <span className="w-2.5 h-2.5 rounded-sm bg-[#f5ad6a]" />
                    <span>{t.dayTime}</span>
                  </span>
                  <span className="font-bold text-white">{dailyTotals.day} kWh</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-1 text-slate-300">
                    <span className="w-2.5 h-2.5 rounded-sm bg-[#8eb367]" />
                    <span>{t.lifeTime}</span>
                  </span>
                  <span className="font-bold text-white">{dailyTotals.life} kWh</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-1 text-slate-300">
                    <span className="w-2.5 h-2.5 rounded-sm bg-[#3071b7]" />
                    <span>{t.nightTime}</span>
                  </span>
                  <span className="font-bold text-white">{dailyTotals.night} kWh</span>
                </div>
              </div>
            </div>
          )}

          {period === 'hourly' && (
            <div className="flex items-center gap-1 bg-slate-950/70 p-1.5 rounded-2xl border border-slate-800">
              <button
                type="button"
                onClick={() => handleToggleHourlyDate('prev')}
                className="px-2 py-1 text-xs text-slate-300 hover:text-white flex items-center gap-1 rounded-lg hover:bg-slate-800"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                <span>{t.prevDay}</span>
              </button>
              <span className="text-xs font-mono font-bold text-teal-400 px-2">
                {formatDayLabel(hourlyDateStr)}
              </span>
              <button
                type="button"
                onClick={() => handleToggleHourlyDate('next')}
                className="px-2 py-1 text-xs text-slate-300 hover:text-white flex items-center gap-1 rounded-lg hover:bg-slate-800"
              >
                <span>{t.nextDay}</span>
                <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* ------------------------------------------------------------- */}
        {/* CHART RENDERING SECTION                                       */}
        {/* ------------------------------------------------------------- */}

        {/* VIEW 1: MONTHLY CHART */}
        {period === 'monthly' && (
          <div className="space-y-4">
            <div className="relative h-56 w-full select-none pt-4 flex flex-col">
              {/* 1. Bar Plot Container */}
              <div className="relative flex-1 w-full min-h-0">
                {/* Background grid spanning plot area */}
                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-15">
                  <div className="border-b border-slate-600 w-full" />
                  <div className="border-b border-slate-600 w-full" />
                  <div className="border-b border-slate-600 w-full" />
                  <div className="border-b border-slate-600 w-full" />
                </div>

                {/* Monthly Stacked Bar Chart */}
                {displayUnit === 'amount' ? (
                  <div className="h-full w-full flex items-end justify-between gap-1 sm:gap-2 px-1">
                    {monthlyChart.map((m, idx) => {
                      const isSelected = selectedMonthIndex === idx;

                      // Current year stack heights (night, life, day)
                      const curNightPct = Math.min(100, ((m.nightKwh || 0) / maxMonthlyVal) * 100);
                      const curLifePct = Math.min(100, ((m.lifeKwh || 0) / maxMonthlyVal) * 100);
                      const curDayPct = Math.min(100, ((m.dayKwh || 0) / maxMonthlyVal) * 100);

                      // Last year stack heights
                      const prevNightPct = Math.min(100, ((m.prevNightKwh || 0) / maxMonthlyVal) * 100);
                      const prevLifePct = Math.min(100, ((m.prevLifeKwh || 0) / maxMonthlyVal) * 100);
                      const prevDayPct = Math.min(100, ((m.prevDayKwh || 0) / maxMonthlyVal) * 100);

                      return (
                        <div
                          key={`m-amt-${m?.timestamp || m?.label || idx}-${idx}`}
                          onClick={() => setPinnedMonthIndex(idx)}
                          className="flex-1 h-full flex flex-col justify-end items-center group cursor-pointer relative"
                        >
                          {/* Hover / Select Tooltip */}
                          {isSelected && (
                            <div className="absolute -top-12 z-30 whitespace-nowrap bg-slate-950 text-white text-[10px] font-semibold py-1 px-2.5 rounded-xl shadow-2xl border border-teal-500/50 pointer-events-none font-mono">
                              <div>{lang === 'ja' ? m.label : formatMonthLabel(m.label, m.timestamp)}: {m.kwh} kWh</div>
                              <div className="text-slate-400 font-normal text-[9px]">
                                {lang === 'ja'
                                  ? `昼 ${roundKwh(m.dayKwh)} / 生 ${roundKwh(m.lifeKwh)} / 夜 ${roundKwh(m.nightKwh)} kWh`
                                  : `Day ${roundKwh(m.dayKwh)} / Life ${roundKwh(m.lifeKwh)} / Night ${roundKwh(m.nightKwh)} kWh`}
                              </div>
                            </div>
                          )}

                          {/* Pair of bars: Last Year & This Year */}
                          <div className="flex items-end justify-center gap-0.5 sm:gap-1 w-full max-w-[28px] h-full">
                            {/* Last Year Bar (stacked) */}
                            <div className="flex-1 flex flex-col justify-end h-full">
                              {prevDayPct > 0 && <div style={{ height: `${prevDayPct}%` }} className="w-full bg-[#fec794] rounded-t-xs" />}
                              {prevLifePct > 0 && <div style={{ height: `${prevLifePct}%` }} className="w-full bg-[#badf92]" />}
                              {prevNightPct > 0 && <div style={{ height: `${prevNightPct}%` }} className="w-full bg-[#7b9ec3] rounded-b-xs" />}
                            </div>

                            {/* This Year Bar (stacked) */}
                            <div className={`flex-1 flex flex-col justify-end h-full rounded-t-sm transition-all ${isSelected ? 'ring-2 ring-teal-400 brightness-110 shadow-lg' : 'group-hover:brightness-110'}`}>
                              {curDayPct > 0 && <div style={{ height: `${curDayPct}%` }} className="w-full bg-[#f5ad6a] rounded-t-xs" />}
                              {curLifePct > 0 && <div style={{ height: `${curLifePct}%` }} className="w-full bg-[#8eb367]" />}
                              {curNightPct > 0 && <div style={{ height: `${curNightPct}%` }} className="w-full bg-[#3071b7] rounded-b-xs" />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Monthly Cost Area / Trend Bars */
                  <div className="h-full w-full flex items-end justify-between gap-1 sm:gap-2 px-1">
                    {monthlyChart.map((m, idx) => {
                      const isSelected = selectedMonthIndex === idx;
                      const curCostPct = Math.min(100, (m.cost / maxMonthlyVal) * 100);
                      const prevCostPct = Math.min(100, ((m.prevYearCost || 0) / maxMonthlyVal) * 100);

                      return (
                        <div
                          key={`m-cost-${m?.timestamp || m?.label || idx}-${idx}`}
                          onClick={() => setPinnedMonthIndex(idx)}
                          className="flex-1 h-full flex flex-col justify-end items-center cursor-pointer group relative"
                        >
                          {isSelected && (
                            <div className="absolute -top-12 z-30 whitespace-nowrap bg-slate-950 text-white text-[10px] font-semibold py-1 px-2.5 rounded-xl shadow-2xl border border-[#ed7170] pointer-events-none font-mono">
                              <div>{lang === 'ja' ? m.label : formatMonthLabel(m.label, m.timestamp)}: ¥{m.cost.toLocaleString()}</div>
                              {m.prevYearCost ? (
                                <div className="text-slate-400 font-normal text-[9px]">{lang === 'ja' ? '前年:' : 'Last Year:'} ¥{m.prevYearCost.toLocaleString()}</div>
                              ) : null}
                            </div>
                          )}

                          <div className="flex items-end justify-center gap-1 w-full max-w-[24px] h-full">
                            {/* Last Year */}
                            {prevCostPct > 0 && (
                              <div
                                style={{ height: `${prevCostPct}%` }}
                                className="flex-1 bg-[#82acbf] rounded-t-xs opacity-80"
                              />
                            )}
                            {/* This Year */}
                            <div
                              style={{ height: `${curCostPct}%` }}
                              className={`flex-1 bg-[#ed7170] rounded-t-xs transition-all ${isSelected ? 'ring-2 ring-white shadow-lg brightness-110' : 'group-hover:brightness-110'}`}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Single Continuous Baseline Divider */}
              <div className="w-full border-t border-slate-700/80 shrink-0" />

              {/* 2. Decoupled X-Axis Label Row */}
              <div className="h-6 w-full flex items-center justify-between gap-1 sm:gap-2 px-1 shrink-0 pt-1">
                {monthlyChart.map((m, idx) => {
                  const isSelected = selectedMonthIndex === idx;
                  const shortLabel = getMonthShortAbbr(m.label, m.timestamp);

                  return (
                    <div
                      key={`m-lbl-${idx}`}
                      onClick={() => setPinnedMonthIndex(idx)}
                      className="flex-1 flex justify-center items-center cursor-pointer"
                    >
                      <span className={`text-[10px] font-mono transition-colors ${
                        displayUnit === 'amount'
                          ? (isSelected ? 'text-teal-300 font-bold' : 'text-slate-500')
                          : (isSelected ? 'text-[#ed7170] font-bold' : 'text-slate-500')
                      }`}>
                        {shortLabel}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Portal statement ledger: one row per confirmed 請求年月 */}
            <div className="pt-3 border-t border-slate-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-white">{t.monthlyListTitle}</span>
                <span className="text-[10px] text-slate-400">{t.monthlyListSubtitle}</span>
              </div>

              <div className="overflow-x-auto rounded-2xl border border-slate-800">
                <table className="w-full text-xs text-center border-collapse">
                  <thead>
                    <tr className="bg-slate-950/80 text-slate-400 font-semibold border-b border-slate-800">
                      <th className="py-2.5 px-3 text-left">{t.tableBillingMonth}</th>
                      <th className="py-2.5 px-2">{t.tableReadingDate}</th>
                      <th className="py-2.5 px-2">{t.tableDaysCount}</th>
                      <th className="py-2.5 px-2">{t.tableUsageKwh}</th>
                      <th className="py-2.5 px-2">{t.tableDailyAvg}</th>
                      <th className="py-2.5 px-2">{t.tableBilledAmount}</th>
                      <th className="py-2.5 px-2">{t.tableExclAdj}</th>
                      <th className="py-2.5 px-2">{t.tableCsv}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60 font-mono">
                    {statementRows.length === 0 ? (
                      <tr>
                        <td colSpan={8} className="py-6 text-center text-slate-500 font-sans">
                          {lang === 'ja' ? 'まだ確定した請求明細はありません。' : 'No confirmed statements yet.'}
                        </td>
                      </tr>
                    ) : (
                      statementRows.slice().reverse().map((m, idx) => {
                      const avgDaily =
                        m?.dailyAvgKwh ?? (m?.daysCount ? Number(((m?.kwh || 0) / m.daysCount).toFixed(1)) : 0);
                      return (
                        <tr
                          key={`m-row-${m?.timestamp || m?.label || idx}-${idx}`}
                          className="hover:bg-slate-800/40 transition-colors cursor-pointer group"
                          onClick={() => {
                            setPeriod('daily');
                          }}
                        >
                          <td className="py-2.5 px-3 text-left text-teal-400 font-bold group-hover:underline">
                            {lang === 'ja' ? (m?.label || '') : formatMonthLabel(m?.label || '', m?.timestamp)}
                          </td>
                          <td className="py-2.5 px-2 text-slate-300">{formatReadingDate(m?.readingDate || '')}</td>
                          <td className="py-2.5 px-2 text-slate-300">{m?.daysCount ? `${m.daysCount} ${t.daysUnit}` : '-'}</td>
                          <td className="py-2.5 px-2 text-white font-bold">{roundKwh(m?.kwh)}</td>
                          <td className="py-2.5 px-2 text-slate-400">{avgDaily}</td>
                          <td className="py-2.5 px-2 text-white font-bold">¥{(m?.cost || 0).toLocaleString()}</td>
                          <td className="py-2.5 px-2 text-slate-400">¥{(m?.amountExcludingLevy || 0).toLocaleString()}</td>
                          <td className="py-2.5 px-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadCsv(lang === 'ja' ? m.label : formatMonthLabel(m.label, m.timestamp));
                              }}
                              className="text-[10px] text-teal-400 hover:text-teal-200 bg-teal-950/60 border border-teal-800/50 rounded px-2 py-0.5 hover:bg-teal-900 transition-colors"
                            >
                              DL
                            </button>
                          </td>
                        </tr>
                      );
                    }))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* SymEnergy Power Procurement Adjustment Surcharge Estimator */}
            <div className="pt-2">
              <SurchargeCalculatorCard defaultKwh={activeMonth?.kwh || 0} lang={lang} />
            </div>
          </div>
        )}

        {/* VIEW 2: DAILY CHART */}
        {period === 'daily' && (
          dailyData.length === 0 ? (
            <div className="py-12 px-4 text-center border border-dashed border-slate-800 rounded-2xl bg-slate-950/40 my-4">
              <p className="text-sm font-bold text-slate-300">
                {lang === 'ja' ? 'データがありません' : 'No Data Available'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {lang === 'ja' ? 'このご使用場所の対象期間データは存在しません。' : 'No telemetry data recorded for this location.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
            <div className="relative h-56 w-full select-none pt-4 flex flex-col">
              {/* 1. Bar Plot Container (All bars live here, guaranteed identical height and baseline) */}
              <div className="relative flex-1 w-full min-h-0">
                {/* Background horizontal grid lines */}
                <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-15">
                  <div className="border-b border-slate-600 w-full" />
                  <div className="border-b border-slate-600 w-full" />
                  <div className="border-b border-slate-600 w-full" />
                  <div className="border-b border-slate-600 w-full" />
                </div>

                {/* Bars Row: all bars sit directly on the baseline */}
                <div className="h-full w-full flex items-end justify-between gap-0.5 sm:gap-1 px-1">
                  {dailyData.map((d, idx) => {
                    const isSelected = selectedDayIndex === idx;
                    const totalKwh = d?.kwh ?? ((d.dayKwh || 0) + (d.lifeKwh || 0) + (d.nightKwh || 0));
                    const heightPct = Math.min(100, Math.max(totalKwh > 0 ? 3 : 0, (totalKwh / maxDailyVal) * 100));

                    const sumSubKwh = (d.dayKwh || 0) + (d.lifeKwh || 0) + (d.nightKwh || 0);
                    const dayRatio = sumSubKwh > 0 ? (d.dayKwh || 0) / sumSubKwh : 0;
                    const lifeRatio = sumSubKwh > 0 ? (d.lifeKwh || 0) / sumSubKwh : 0;
                    const nightRatio = sumSubKwh > 0 ? (d.nightKwh || 0) / sumSubKwh : 0;
                    const hasSubTiers = dayRatio > 0 || lifeRatio > 0 || nightRatio > 0;

                    return (
                      <div
                        key={`d-bar-${d?.label || idx}-${idx}`}
                        onClick={() => setSelectedDayIndex(idx)}
                        className="flex-1 h-full flex flex-col justify-end items-center cursor-pointer group relative"
                      >
                        {/* Hover / Select Tooltip */}
                        {isSelected && (
                          <div className="absolute -top-12 z-30 whitespace-nowrap bg-slate-950 text-white text-[10px] font-semibold py-1 px-2.5 rounded-xl shadow-2xl border border-teal-400 pointer-events-none font-mono">
                            <div>{formatDayLabel(d.label)}: {d.kwh} kWh</div>
                            <div className="text-slate-400 text-[9px]">
                              {lang === 'ja'
                                ? `昼 ${roundKwh(d.dayKwh)} / 生 ${roundKwh(d.lifeKwh)} / 夜 ${roundKwh(d.nightKwh)}`
                                : `Day ${roundKwh(d.dayKwh)} / Life ${roundKwh(d.lifeKwh)} / Night ${roundKwh(d.nightKwh)}`}
                            </div>
                          </div>
                        )}

                        {/* Stacked Bar Plot */}
                        <div
                          style={{ height: `${heightPct}%` }}
                          className={`w-full max-w-[14px] flex flex-col justify-end rounded-t-sm overflow-hidden transition-all ${
                            isSelected ? 'ring-2 ring-teal-400 shadow-lg brightness-110' : 'group-hover:brightness-110'
                          }`}
                        >
                          {hasSubTiers ? (
                            <>
                              {dayRatio > 0 && <div style={{ height: `${dayRatio * 100}%` }} className="w-full bg-[#f5ad6a]" />}
                              {lifeRatio > 0 && <div style={{ height: `${lifeRatio * 100}%` }} className="w-full bg-[#8eb367]" />}
                              {nightRatio > 0 && <div style={{ height: `${nightRatio * 100}%` }} className="w-full bg-[#3071b7]" />}
                            </>
                          ) : (
                            totalKwh > 0 && <div className="w-full h-full bg-[#3071b7]" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Single Continuous Horizontal Baseline Divider */}
              <div className="w-full border-t border-slate-700/80 shrink-0" />

              {/* 2. Decoupled X-Axis Label Row */}
              <div className="h-6 w-full flex items-center justify-between gap-0.5 sm:gap-1 px-1 shrink-0 pt-1">
                {dailyData.map((d, idx) => {
                  const isSelected = selectedDayIndex === idx;
                  const shortLabel = d.label.split('(')[0];
                  const hasLabel = idx % 5 === 0 || idx === dailyData.length - 1;

                  return (
                    <div
                      key={`d-lbl-${d?.label || idx}-${idx}`}
                      onClick={() => setSelectedDayIndex(idx)}
                      className="flex-1 flex justify-center items-center cursor-pointer"
                    >
                      {hasLabel && (
                        <span className={`text-[9px] font-mono leading-none ${
                          isSelected ? 'text-teal-300 font-bold' : 'text-slate-500'
                        }`}>
                          {shortLabel}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Official SymEnergy Daily List Table */}
            <div className="pt-3 border-t border-slate-800">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-white">{t.dailyListTitle}</span>
                <span className="text-[10px] text-teal-400">{t.dailyListSubtitle}</span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs font-mono mb-3">
                <div className="p-2.5 rounded-xl bg-slate-950/70 border border-slate-800 flex justify-between">
                  <span className="text-slate-400">{t.periodTotal}</span>
                  <span className="font-bold text-white">{dailyTotals.total} kWh</span>
                </div>
                <div className="p-2.5 rounded-xl bg-slate-950/70 border border-slate-800 flex justify-between">
                  <span className="text-slate-400">{t.tableDailyAvg}</span>
                  <span className="font-bold text-teal-400">{dailyTotals.avg} kWh</span>
                </div>
              </div>

              <div className="max-h-56 overflow-y-auto rounded-2xl border border-slate-800">
                <table className="w-full text-xs text-center border-collapse">
                  <thead className="sticky top-0 bg-slate-950 z-10 text-slate-400 border-b border-slate-800">
                    <tr>
                      <th className="py-2 px-3 text-left">{t.dateCol}</th>
                      <th className="py-2 px-2">{t.tableUsageKwh}</th>
                      <th className="py-2 px-2">{t.dayTime}</th>
                      <th className="py-2 px-2">{t.lifeTime}</th>
                      <th className="py-2 px-2">{t.nightTime}</th>
                      <th className="py-2 px-2">{t.estCostCol}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60 font-mono">
                    {dailyData.slice().reverse().map((d, idx) => (
                      <tr
                        key={`d-row-${d?.label || idx}-${idx}`}
                        onClick={() => {
                          if (d?.label) {
                            const foundIdx = availableHourlyDates.findIndex(item => item.raw === d.label);
                            if (foundIdx >= 0) {
                              setHourlyDateIdx(foundIdx);
                            }
                          }
                          setPeriod('hourly');
                        }}
                        className="hover:bg-slate-800/40 cursor-pointer transition-colors group"
                      >
                        <td className="py-2 px-3 text-left text-teal-400 font-bold group-hover:underline">
                          {formatDayLabel(d?.label || '')}
                        </td>
                        <td className="py-2 px-2 text-white font-bold">{roundKwh(d?.kwh)}</td>
                        <td className="py-2 px-2 text-amber-300">{roundKwh(d?.dayKwh)}</td>
                        <td className="py-2 px-2 text-emerald-300">{roundKwh(d?.lifeKwh)}</td>
                        <td className="py-2 px-2 text-blue-300">{roundKwh(d?.nightKwh)}</td>
                        <td className="py-2 px-2 text-slate-300">¥{(d?.cost || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}

        {/* VIEW 3: HOURLY CHART */}
        {period === 'hourly' && (
          availableHourlyDates.length === 0 ? (
            <div className="py-12 px-4 text-center border border-dashed border-slate-800 rounded-2xl bg-slate-950/40 my-4">
              <p className="text-sm font-bold text-slate-300">
                {lang === 'ja' ? 'データがありません' : 'No Data Available'}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {lang === 'ja' ? 'このご使用場所の対象期間データは存在しません。' : 'No telemetry data recorded for this location.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative h-56 w-full select-none pt-4 flex flex-col">
                {/* 1. Bar Plot Container (All bars live here, guaranteed identical height and baseline) */}
                <div className="relative flex-1 w-full min-h-0">
                  {/* Background horizontal grid lines */}
                  <div className="absolute inset-0 flex flex-col justify-between pointer-events-none opacity-15">
                    <div className="border-b border-slate-600 w-full" />
                    <div className="border-b border-slate-600 w-full" />
                    <div className="border-b border-slate-600 w-full" />
                    <div className="border-b border-slate-600 w-full" />
                  </div>

                  {/* 24 Hourly Bars */}
                  <div className="h-full w-full flex items-end justify-between gap-1 px-1">
                    {activeHourlyData.map((h, hour) => {
                      const isSelected = selectedHourIndex === hour;
                      const heightPct = Math.min(100, Math.max((h?.kwh || 0) > 0 ? 3 : 0, (((h?.kwh || 0)) / maxHourlyVal) * 100));

                      // Determine color based on weekday 3-tier or weekend
                      let barColor = '#3071b7';
                      let tierName = lang === 'ja' ? 'ナイトタイム (夜時間)' : 'Night Time (Off-Peak)';
                      if (!isHourlyWeekend) {
                        // Weekday: 9-16 Day (#f5ad6a), 6-9 & 16-23 Life (#8eb367), 23-6 Night (#3071b7)
                        if (hour >= 9 && hour < 16) {
                          barColor = '#f5ad6a';
                          tierName = lang === 'ja' ? 'デイタイム (昼時間)' : 'Day Time (09:00–16:00)';
                        } else if ((hour >= 6 && hour < 9) || (hour >= 16 && hour < 23)) {
                          barColor = '#8eb367';
                          tierName = lang === 'ja' ? 'ライフタイム (生活時間)' : 'Life Time (06-09 / 16-23)';
                        }
                      } else {
                        // Weekend: 8-22 Day (#f5ad6a), 22-8 Night (#3071b7)
                        if (hour >= 8 && hour < 22) {
                          barColor = '#f5ad6a';
                          tierName = lang === 'ja' ? 'デイタイム (休日昼間)' : 'Day Time (Weekend 08:00–22:00)';
                        }
                      }

                      return (
                        <div
                          key={hour}
                          onClick={() => setSelectedHourIndex(hour)}
                          className="flex-1 h-full flex flex-col justify-end items-center cursor-pointer group relative"
                        >
                          {isSelected && (
                            <div className="absolute -top-12 z-30 whitespace-nowrap bg-slate-950 text-white text-[10px] font-semibold py-1 px-2.5 rounded-xl shadow-2xl border border-teal-400 pointer-events-none font-mono">
                              <div>{hour}:00 {lang === 'ja' ? '〜' : '–'} {hour + 1}:00: {h?.kwh ?? 0} kWh</div>
                              <div className="text-slate-300 text-[9px]">{tierName}</div>
                            </div>
                          )}

                          <div
                            style={{ height: `${heightPct}%`, backgroundColor: barColor }}
                            className={`w-full max-w-[14px] rounded-t-sm transition-all ${
                              isSelected ? 'ring-2 ring-white shadow-lg brightness-110' : 'group-hover:brightness-110'
                            }`}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Single Continuous Horizontal Baseline Divider */}
                <div className="w-full border-t border-slate-700/80 shrink-0" />

                {/* 2. Decoupled X-Axis Label Row */}
                <div className="h-6 w-full flex items-center justify-between gap-1 px-1 shrink-0 pt-1">
                  {activeHourlyData.map((h, hour) => {
                    const isSelected = selectedHourIndex === hour;
                    const hasLabel = hour % 3 === 0 || hour === 23;

                    return (
                      <div
                        key={`h-lbl-${hour}`}
                        onClick={() => setSelectedHourIndex(hour)}
                        className="flex-1 flex justify-center items-center cursor-pointer"
                      >
                        {hasLabel && (
                          <span className={`text-[9px] font-mono leading-none ${
                            isSelected ? 'text-teal-300 font-bold' : 'text-slate-500'
                          }`}>
                            {hour}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Hourly Legend & 3-Tier Explanation */}
              <div className="pt-3 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-3">
                  <span className="flex items-center gap-1.5 text-slate-300">
                    <span className="w-2.5 h-2.5 rounded-sm bg-[#3071b7]" />
                    <span>{t.nightTime} ({lang === 'ja' ? '夜間: ¥18.88' : 'Off-Peak: ¥18.88'})</span>
                  </span>
                  <span className="flex items-center gap-1.5 text-slate-300">
                    <span className="w-2.5 h-2.5 rounded-sm bg-[#f5ad6a]" />
                    <span>{t.dayTime} ({lang === 'ja' ? '昼間: ¥26.25' : 'Day: ¥26.25'})</span>
                  </span>
                  {!isHourlyWeekend && (
                    <span className="flex items-center gap-1.5 text-slate-300">
                      <span className="w-2.5 h-2.5 rounded-sm bg-[#8eb367]" />
                      <span>{t.lifeTime} ({lang === 'ja' ? '生活: ¥32.65' : 'Life: ¥32.65'})</span>
                    </span>
                  )}
                </div>

                <span className="text-[11px] text-slate-500 font-mono">
                  {isHourlyWeekend 
                    ? (lang === 'ja' ? '休日パターン (デイ・ナイト2区分)' : 'Weekend 2-Tier (Day/Night)') 
                    : (lang === 'ja' ? '平日3段階パターン (昼・生活・夜)' : 'Weekday 3-Tier (Day/Life/Night)')}
                </span>
              </div>
            </div>
          )
        )}
      </div>

      {/* Power Source Mix & Non-Fossil Certificate (Official FAQ Feature) */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-xl space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-teal-500/15 text-teal-400 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">{t.powerMixTitle}</h4>
              <p className="text-xs text-slate-400">{t.powerMixSubtitle}</p>
            </div>
          </div>
          <span className="text-[11px] font-mono font-bold text-teal-300 bg-teal-950 border border-teal-800/60 rounded px-2 py-0.5">
            100% RE
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 text-xs">
          <div className="p-3 bg-slate-950/60 rounded-2xl border border-slate-800">
            <p className="text-[10px] text-slate-400">{t.powerBiomass}</p>
            <p className="text-base font-bold text-teal-300 font-mono mt-0.5">42%</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{lang === 'ja' ? '国内地域循環材' : 'Local Eco Biomass'}</p>
          </div>
          <div className="p-3 bg-slate-950/60 rounded-2xl border border-slate-800">
            <p className="text-[10px] text-slate-400">{t.powerSolar}</p>
            <p className="text-base font-bold text-amber-400 font-mono mt-0.5">31%</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{lang === 'ja' ? 'FIT電気等調達' : 'FIT Clean Solar'}</p>
          </div>
          <div className="p-3 bg-slate-950/60 rounded-2xl border border-slate-800">
            <p className="text-[10px] text-slate-400">{t.powerWind}</p>
            <p className="text-base font-bold text-cyan-400 font-mono mt-0.5">17%</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{lang === 'ja' ? '地域小形風力' : 'Regional Wind/Geo'}</p>
          </div>
          <div className="p-3 bg-slate-950/60 rounded-2xl border border-slate-800">
            <p className="text-[10px] text-slate-400">{t.powerNonFossil}</p>
            <p className="text-base font-bold text-emerald-400 font-mono mt-0.5">100%</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{lang === 'ja' ? '環境価値付帯' : '100% Non-Fossil'}</p>
          </div>
        </div>
      </div>

      {/* Environmental CO2 Savings Card */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-teal-950/40 border border-slate-800 rounded-3xl p-5 shadow-xl">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
              <Leaf className="w-4 h-4" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-white">{t.co2Title}</h4>
              <p className="text-xs text-slate-400">{t.co2Subtitle}</p>
            </div>
          </div>
          <span className="text-xs font-semibold text-teal-400 bg-teal-950/60 border border-teal-800/40 rounded-lg px-2.5 py-1">
            {t.efficiencyLevel}
          </span>
        </div>

        <div className="grid grid-cols-3 gap-2.5 pt-2">
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-3 text-center">
            <p className="text-[10px] text-slate-400">{t.co2Avoided}</p>
            <p className="text-base font-bold text-teal-300 font-mono mt-0.5">
              {(currentTotalKwh * 0.43).toFixed(1)} {t.co2Unit}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">{t.co2Offset}</p>
          </div>
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-3 text-center">
            <p className="text-[10px] text-slate-400">{t.efficiencyScore}</p>
            <p className="text-base font-bold text-emerald-400 font-mono mt-0.5">
              +14.2%
            </p>
            <p className="text-[10px] text-teal-400 mt-0.5">{lang === 'ja' ? 'エリア平均比' : 'vs Area Avg'}</p>
          </div>
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-2xl p-3 text-center">
            <p className="text-[10px] text-slate-400">{t.baseloadFactor}</p>
            <p className="text-base font-bold text-slate-200 font-mono mt-0.5">
              8.0%
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">{lang === 'ja' ? '待機電力抑制' : 'Low Idle'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
